if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 *
 *  Sticky header and the height variables other files read:
 *  --announcement-top-height  real height of the sticky top announcement bar (countdown bar offset)
 *  --announcement-height      sticky bars above the header (header top, hero height, scroll padding)
 *  --header-height            header bar height
 *  --header-offset            distance from the viewport top to the header
 *  Heights are measured, never fixed: the AoA static bar may wrap to two lines on phones.
 *  ResizeObserver re-measures on wrap, font swap and resize. This file loads with defer: the first
 *  values come from the small inline script after <theme-header> in sections/header.liquid, so they
 *  exist before the hero is laid out (no shift).
 *  The mobile menu and the mega panels live in assets/aoa-header.js.
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.setAnnouncementHeight = this.setAnnouncementHeight.bind(this);
      this.values = {};
    }
    connectedCallback() {
      this.header_section = this.closest('.header-section') || document.querySelector('.header-section');
      this.stickyOffset = null;
      this.lastScrollY = window.scrollY; // starting point of setHiddenOnScroll (no layout read)

      window.addEventListener('scroll', this.onScroll, { passive: true });
      // Keyboard focus reaching the hidden header brings it back
      this.addEventListener('focusin', () => document.documentElement.classList.remove('aoa-header-hidden'));

      this.applyStickyClasses();
      // First measures in the next frame, reads before writes: measuring right after the class
      // writes above forced a synchronous layout on load (50-71 ms with defer)
      requestAnimationFrame(() => {
        // Offset for the sticky class: a transparent header (position: absolute) starts at 0
        if (this.classList.contains('header-sticky--active')) {
          this.stickyOffset = this.classList.contains('transparent--true') ? 0 : this.offsetTop;
        }
        this.batchVars(() => {
          this.setAnnouncementHeight();
          this.setHeaderHeight();
          this.setHeaderOffset();
        });
        this.setStickyClass();
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.setAnnouncementHeight();
          this.setHeaderHeight();
        });
        this.resizeObserver.observe(this);
        this.observeBars();
      } else {
        this.onResize = debounce(() => {
          this.setAnnouncementHeight();
          this.setHeaderHeight();
        }, 100);
        window.addEventListener('resize', this.onResize);
      }
      this.observeStickyChanges();

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.onSectionLoad);
        document.addEventListener('shopify:section:unload', this.onSectionLoad);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionLoad);
    }
    // Theme editor: an announcement bar was re-rendered, so its element changed
    onSectionLoad() {
      requestAnimationFrame(() => {
        this.applyStickyClasses();
        this.observeBars();
        this.observeStickyChanges();
        this.setAnnouncementHeight();
      });
    }
    observeBars() {
      if (!this.resizeObserver) return;
      (this.observedBars || []).forEach((bar) => this.resizeObserver.unobserve(bar));
      this.observedBars = Array.from(document.querySelectorAll('.announcement-bar-top-section, .announcement-bar-countdown-section'));
      this.observedBars.forEach((bar) => this.resizeObserver.observe(bar));
    }
    // Writes a custom property on <html> only when its value changes
    setVar(name, value) {
      if (this.values[name] === value) return;
      this.values[name] = value;
      if (this.pendingVars) {
        this.pendingVars[name] = value;
        return;
      }
      document.documentElement.style.setProperty(name, value);
    }
    // Runs the measures in fn and writes their custom properties only at the end
    batchVars(fn) {
      this.pendingVars = {};
      try {
        fn();
      } finally {
        const pending = this.pendingVars;
        this.pendingVars = null;
        Object.keys(pending).forEach((name) => document.documentElement.style.setProperty(name, pending[name]));
      }
    }
    onScroll() {
      this.setStickyClass();
      this.setHeaderOffset();
      this.setHiddenOnScroll();
    }

    // Approved mockup (01 Header): the sticky bars slide up when scrolling down and come back when
    // scrolling up. Always shown in the first 120px, while a menu or panel is open or focus is inside.
    // The class goes on <html>; assets/aoa-header.css moves the bars (no motion with reduced motion).
    setHiddenOnScroll() {
      const y = window.scrollY;
      if (Math.abs(y - this.lastScrollY) < 6) return;
      const down = y > this.lastScrollY;
      this.lastScrollY = y;
      const keep =
        !this.classList.contains('header-sticky--active') ||
        y < 120 ||
        this.classList.contains('aoa-header--panel-open') ||
        this.matches(':focus-within');
      const hide = !keep && down;
      document.documentElement.classList.toggle('aoa-header-hidden', hide);
    }

    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        const offset = this.stickyOffset || 0;
        const isSticky = window.scrollY > offset;
        if (this.classList.contains('is-sticky') !== isSticky) {
          this.classList.toggle('is-sticky', isSticky);
        }

        // Hide or show the countdown when the header is sticky (if enabled)
        if (this.classList.contains('hide-countdown-when-sticky')) {
          const countdown_bar = document.querySelector('.announcement-bar-countdown-section');
          if (countdown_bar && countdown_bar.classList.contains('hidden-when-sticky') !== isSticky) {
            countdown_bar.classList.toggle('hidden-when-sticky', isSticky);
            if (isSticky) {
              countdown_bar.setAttribute('data-hidden-sticky', 'true');
            } else {
              countdown_bar.removeAttribute('data-hidden-sticky');
            }
            this.setAnnouncementHeight();
          }
        }
      }
    }
    applyStickyClasses() {
      // Sticky class on the top announcement bar wrapper, from its data attribute
      const topBar = document.querySelector('.announcement-bar-top[data-sticky-enabled]');
      if (topBar) {
        const topSection = topBar.closest('.announcement-bar-top-section');
        if (topSection) {
          topSection.classList.toggle('sticky-enabled-section', topBar.getAttribute('data-sticky-enabled') === 'true');
        }
      }

      // Same for the countdown bar
      const countdownBar = document.querySelector('.announcement-bar-countdown[data-sticky-enabled]');
      if (countdownBar) {
        const countdownSection = countdownBar.closest('.announcement-bar-countdown-section');
        if (countdownSection) {
          countdownSection.classList.toggle('sticky-enabled-section', countdownBar.getAttribute('data-sticky-enabled') === 'true');
        }
      }
    }
    observeStickyChanges() {
      // A sticky class added or removed changes what counts in --announcement-height
      if (!('MutationObserver' in window)) return;
      if (!this.mutationObserver) {
        this.mutationObserver = new MutationObserver(this.setAnnouncementHeight);
      }
      this.mutationObserver.disconnect();
      document.querySelectorAll('.announcement-bar-top-section, .announcement-bar-countdown-section').forEach((section) => {
        this.mutationObserver.observe(section, { attributes: true, attributeFilter: ['class'] });
      });
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-top-section');
      const countdown_bar = document.querySelector('.announcement-bar-countdown-section');
      let h = 0;
      let top_h = 0;

      // Real height of the top bar when it is sticky; in the normal flow it needs no reserved space
      if (a_bar && a_bar.classList.contains('sticky-enabled-section')) {
        top_h = a_bar.getBoundingClientRect().height;
        h += top_h;
      }
      this.setVar('--announcement-top-height', top_h + 'px');

      if (countdown_bar && countdown_bar.classList.contains('sticky-enabled-section')) {
        const computedStyle = window.getComputedStyle(countdown_bar);
        if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden') {
          h += countdown_bar.getBoundingClientRect().height;
        }
      }

      this.setVar('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      if (this.header_section) {
        const h = this.header_section.getBoundingClientRect().top;
        this.setVar('--header-offset', h + 'px');
      }
    }
    setHeaderHeight() {
      this.setVar('--header-height', this.clientHeight + 'px');
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      window.addEventListener('resize', debounce(function () {
        _this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(function () {
        _this.resizeSubMenus();
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
